import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Alert, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import firebase from 'firebase/compat/app';
import 'firebase/compat/storage';
import 'firebase/compat/firestore';
import 'firebase/compat/auth';

interface SignaturePadProps {
  onSave: (signatureUrl: string) => void;
}

const SignaturePad: React.FC<SignaturePadProps> = ({ onSave }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const signatureRef = useRef<SignatureCanvas | null>(null);

  const clearSignature = () => {
      if (signatureRef.current) {
          signatureRef.current.clear();
      }
      setError(null);
  };

  const saveSignature = async () => {
      if (!signatureRef.current || signatureRef.current.isEmpty()) {
          setError("Please draw a signature before saving.");
          return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
          const userId = firebase.auth().currentUser?.uid;
          if (!userId) {
              throw new Error("You must be logged in to save a signature.");
          }

          // Get data URL and convert to blob
          const dataUrl = signatureRef.current.toDataURL();
          const response = await fetch(dataUrl);
          const blob = await response.blob();

          // Upload to Firebase Storage
          const storage = firebase.storage();
          const storageReference = storage.ref(`signatures/${userId}_${Date.now()}.png`);
          await storageReference.put(blob);
          const downloadUrl = await storageReference.getDownloadURL();

          // Save URL to Firestore
          await firebase.firestore().collection('accounts').doc(userId).set({
              signatureUrl: downloadUrl,
              signatureTimestamp: firebase.firestore.FieldValue.serverTimestamp()
          }, { merge: true });

          await onSave(downloadUrl);
          setModalOpen(false);

      } catch (error: unknown) {
          console.error("Error saving signature:", error);
          setError(error instanceof Error ? error.message : "An error occurred while saving the signature.");
      } finally {
          setIsSubmitting(false);
      }
  };

  return (
      <>
          <Button color="primary" onClick={() => setModalOpen(true)}>
              Add Signature
          </Button>
          <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
              <ModalHeader toggle={() => setModalOpen(false)}>
                  Draw Your Signature
              </ModalHeader>
              <ModalBody>
                  {error && (
                      <Alert color="danger" className="mb-3">
                          {error}
                      </Alert>
                  )}
                  <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{
                          width: 500,
                          height: 200,
                          className: 'signature-canvas'
                      }}
                  />
              </ModalBody>
              <ModalFooter>
                  <Button color="secondary" onClick={clearSignature}>
                      Clear
                  </Button>
                  <Button 
                      color="primary" 
                      onClick={saveSignature} 
                      disabled={isSubmitting}
                  >
                      {isSubmitting ? 'Saving...' : 'Save Signature'}
                  </Button>
              </ModalFooter>
          </Modal>
      </>
  );
};

export default SignaturePad;